<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import BaseModal from "@/components/global-themed/BaseModal.vue";
import {computed, ref} from "vue";
import {useMutation, useQuery, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import {useInfiniteScroll} from "@vueuse/core";
import {successToast} from "@/composables/useToasts";

const open = ref(false);
const inputValue = ref('');
const visibleRows = ref(50);
const tableBody = ref(null);

const handleClose = () => {
  open.value = false;
}

const handleOpen = () => {
  open.value = true;

  window.mixpanel('Actions v2: View XML sitemap');
}

const queryClient = useQueryClient();

const { mutate: handleSubmit, isPending: saving } = useMutation({
  mutationFn: async () => {
    await axios.post('/ajax/v2/actions/sitemap-url', {
      url: inputValue.value
    });
  },
  onSuccess: () => {
    successToast(
      'We have now queued the crawler to grab pages from the new URL, check back in 5 minutes to see updated data.',
      'The sitemap URL has been successfully updated',
    );
  },
  onError: () => {
    throw new Error('Something went wrong');
  },
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['website-crawl-data'] });
  },
});

const {data: crawlData, isLoading} = useQuery({
  queryKey: ['website-crawl-data'],
  queryFn: async () => {
    const {data} = await axios.get('/ajax/v2/actions/crawl-data');

    inputValue.value = data.data.sitemap_url;

    return data.data;
  },
});

const sitemapUrls = computed(() =>
  [...crawlData.value?.sitemap ?? []]
    .slice(0, visibleRows.value)
)

useInfiniteScroll(
  computed(() => tableBody.value),
  () => {
    visibleRows.value += 50;
  },
  {
    distance: 200,
  }
)
</script>

<template>
  <DefaultButton
    size="normal"
    class="!tw-px-5 !tw-py-2 tw-h-[42px]"
    @click="handleOpen"
  >
    <span>View</span>
  </DefaultButton>

  <Teleport
    to=".tailwind"
  >
    <BaseModal
      v-if="open"
      @close="handleClose"
    >
      <div class="tw-px-8 tw-pb-8 tw-flex tw-flex-col tw-gap-12 tw-justify-center">
        <div class="tw-space-y-6">
          <h2 class="tw-text-2.5xl tw-text-grey-dark tw-font-bold">
            XML Sitemap Location
          </h2>

          <p class="tw-text-lg">
            Please see below the sitemap URL that we use to grab your XML sitemap, if your XML sitemap is located on a different URL please update it below.
          </p>
        </div>

        <LoadingBlock v-if="isLoading" />

        <div
          v-if="!isLoading"
          class="tw-flex tw-flex-col tw-gap-6"
        >
          <label class="tw-text-lg tw-text-grey-dark tw-font-bold">
            Enter or Update Sitemap URL
          </label>

          <input
            v-model="inputValue"
            class="tw-h-13 tw-border-2 tw-border-grey-mid tw-text-black tw-self-stretch focus-within:tw-border-blue tw-px-5 tw-text-lg tw-w-full tw-rounded-lg"
            type="text"
            @keyup.enter="handleSubmit"
          >

          <DefaultButton
            class="tw-w-auto tw-self-end"
            :disabled="saving"
            @click="handleSubmit"
          >
            Submit
          </DefaultButton>
        </div>

        <div
          v-if="!isLoading && sitemapUrls?.length > 0"
          class="tw-flex tw-flex-col"
        >
          <div
            class="tw-flex tw-gap-4 tw-items-center tw-px-8 tw-py-4 tw-border tw-border-grey-light tw-border-t tw-rounded-t-lg"
          >
            <span class="tw-font-bold tw-text-grey-dark tw-text-sm">
              Pages Found
            </span>
            <span class="tw-bg-blue tw-text-white tw-text-base tw-font-bold tw-min-w-[32px] tw-px-1 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-lg">
              {{ crawlData.sitemap.length }}
            </span>
          </div>
          <div
            ref="tableBody"
            class="tw-max-h-[45vh] tw-overflow-y-scroll"
          >
            <div
              v-for="url in sitemapUrls"
              :key="url"
              class="tw-p-8 tw-border tw-border-t-0 tw-border-grey-light last:tw-rounded-b-lg"
            >
              <a
                :href="url"
                class="tw-text-blue tw-font-bold tw-flex tw-gap-2 tw-items-center"
                target="_blank"
              >
                <span class="tw-underline">
                  {{ url }}
                </span>
                <svg-vue
                  icon="icons/external-thin"
                  class="tw-w-4 tw-h-4 tw-flex-shrink-0 tw-text-blue"
                />
              </a>
            </div>
          </div>
        </div>

        <p
          v-else-if="!crawlData.sitemap_last_crawl"
          class="tw-font-bold tw-text-base"
        >
          We haven't grabbed your sitemap yet, we will do this when we next crawl your website.
        </p>

        <ZeroContent v-else>
          No pages found in the sitemap.
        </ZeroContent>
      </div>
    </BaseModal>
  </Teleport>
</template>
